import { Component, computed, effect, HostListener, inject, signal } from '@angular/core';
import { TopBarItem } from 'src/app/interfaces/top-bar-item';
import { TopBarService } from 'src/app/services/top-bar-service/top-bar-service.service';
import { Navigation2Service } from '../../../services/navigation2.service';
import { BookmarksService } from '../../../services/bookmarks.service';
import { BookmarksComponent } from '../../bookmarks/bookmarks.component';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { SelectedAttributeService } from '../../../services/selected-attribute.service';
import { CheckoutService } from '../../../services/checkout.service';
import { assert } from 'src/app/utils/assert.util';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { Subscription } from 'rxjs';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { InvalidSkuDialogComponent } from '../../invalid-sku.dialog/invalid-sku.dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SkuMapService } from 'src/app/services/sku-map/sku-map.service';
import { SizesService } from 'src/app/services/sizes/sizes.service';
import { formatFingerSizeForB2BSKU } from 'src/app/utils/configured-item.util';
import { IframeService } from 'src/app/services/iframe/iframe.service';
import { DesignExplorerComponent } from '../../design-explorer/design-explorer/design-explorer.component';

@Component({
  selector: 'app-top-bar',
  standalone: false,
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss'
})
export class TopBarComponent {
  private iframeService = inject(IframeService);
  private sizesService = inject(SizesService);
  private skuMapService = inject(SkuMapService);
  private dialog = inject(MatDialog);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private configuredItemService = inject(ConfiguredItemService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private checkoutService = inject(CheckoutService);
  private loadingService = inject(LoadingService);
  bookmarksService = inject(BookmarksService);
  navigationService = inject(Navigation2Service);
  topBarService = inject(TopBarService);
  barItems = computed(() => this.topBarService.items());
  activeItem = computed(() => this.topBarService.activeItem());
  bookmarks = computed(() => this.bookmarksService.bookmarks());
  skuToShow = signal("");
  shouldShowSearch = signal(false);
  b2bDataSubscription: Subscription | undefined;

  constructor() {
    this.topBarService.setItems([
      {
        title: 'Customize',
        key: 'customize',
        action: (barItem: TopBarItem) => {
          this.navigationService.dismissAll();
          this.navigationService.overrideTitle.set(null);
          this.bookmarksService.skusToCompare.set([]);
          this.topBarService.activeItem.set(barItem);
        },
        data: { shouldShowInMobile: true }
      },
      {
        title: 'Compare',
        key: 'favorites',
        action: (barItem: TopBarItem) => {
          this.navigationService.dismissAll();
          const view = { title: barItem.data.viewTitle, type: BookmarksComponent, doesAppearInProgressBar: false };
          this.navigationService.present(view)
          this.navigationService.overrideTitle.set(barItem.data.viewTitle);
          this.topBarService.activeItem.set(barItem);
        },
        data: { viewTitle: 'Favorites', shouldShowInMobile: true }
      },
      {
        title: 'Explore',
        key: 'explore',
        action: (barItem: TopBarItem) => {
          this.navigationService.dismissAll();
          this.navigationService.overrideTitle.set(null);
          this.bookmarksService.skusToCompare.set([]);
          this.topBarService.activeItem.set(barItem);
        },
        data: { shouldShowInMobile: true }
      },
      {
        title: 'Item # Search',
        mobileIcon: 'search',
        key: 'searchToggle',
        action: () => {
          this.navigationService.dismissAll();
          this.navigationService.overrideTitle.set(null);
          this.bookmarksService.skusToCompare.set([]);
          this.topBarService.selectActiveItem('customize');
          this.shouldShowSearch.set(!this.shouldShowSearch())
        }
      }
    ]);

    this.navigationService.subscribe((action) => {
      if (action.action === 'Dismiss' && action.data?.title === this.barItems()[1].data?.viewTitle) {
        this.topBarService.selectActiveItem('customize');
        this.navigationService.overrideTitle.set(null);
        this.bookmarksService.skusToCompare.set([]);
      }
    });
  }

  ngOnInit() {
    const size = this.selectedAttributeService.getValue().size;
    assert(size, 'No size selected');
    this.skuToShow.set((!this.checkoutService.b2bData.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(this.checkoutService.b2bData.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
    this.b2bDataSubscription = this.checkoutService.subscribeToB2BDataChanges(async value => {
      const size = this.selectedAttributeService.getValue().size;
      assert(size, 'No size selected');
      this.loadingService.load('SKUToShow');
      this.skuToShow.set((!value.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(value.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
      this.loadingService.done('SKUToShow');
      console.log(`Current SKU: ${this.skuToShow()}`);
      this.iframeService.sendMessage(JSON.stringify({ type: 'Benchmark::RingLab::skuchange', value: this.skuToShow() }));
    });
    console.log(`Current SKU: ${this.skuToShow()}`);
    this.iframeService.sendMessage(JSON.stringify({ type: 'Benchmark::RingLab::skuchange', value: this.skuToShow() }));
  }

  handleAction(barItem: TopBarItem) {
    if (this.activeItem()?.title !== barItem.title) {
      barItem.action(barItem);
    }
  }

  triggerGAEvent(action: string, title: string, value: string) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent(action, 'top_bar', title, value);
    }
  }

  @HostListener('window:mousedown', ['$event.target'])
  windowClick(target: EventTarget | null) {
    const castTarget = target as HTMLElement;
    if (this.shouldShowSearch() && !castTarget?.classList.contains('ItemSearchBarItem') && !this.doesHaveAncestor(castTarget, (ancestor) => ancestor?.classList?.contains('ItemSearchBarItem') ?? false) && !this.doesHaveAncestor(castTarget, (ancestor) => ancestor?.classList?.contains('SearchContainer') ?? false)) {
      this.shouldShowSearch.set(false);
    }
  }

  doesHaveAncestor(element: HTMLElement, compare: (ancestor: HTMLElement | null) => boolean) {
    let currentElement = element.parentElement;
    let flag = false;
    while (currentElement && !flag) {
      flag = compare(currentElement ?? null);
      currentElement = currentElement.parentElement;
    }
    return flag;
  }
}

